import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {select, Store} from '@ngrx/store';
import {filter, map, mergeMap, switchMap, take, tap, withLatestFrom} from 'rxjs/operators';
import {EMPTY, from, Observable, of} from 'rxjs';
import {
    authenticated,
    initialize,
    isGameMaster,
    loadCharacters,
    reconnect,
    selectCurrentCampaignId,
    selectEventById,
    selectInitialized,
    selectMyCharacterIds,
    setCampaign,
    stateError,
    stateIdle,
    stateUpdate,
    synchronize,
    timelineCreate,
    timelineDelete,
    timelineEvent,
    timelineUpdate,
    websocketActivated,
    websocketClosed,
    websocketOpened
} from 'gamesis-state';
import {RxStomp, RxStompState} from '@stomp/rx-stomp';
import {AuthService} from 'gamesis-auth';
import {environment} from '../../environments/environment';
import {State} from 'gamesis-story-client';
import {NGXLogger} from 'ngx-logger';

@Injectable()
export class WebsocketEffects extends RxStomp {

    activate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authenticated, setCampaign, reconnect),
            withLatestFrom(this.store.select(selectCurrentCampaignId)),
            switchMap(([action, campaign]) => {
                if (campaign) {
                    this.configure({
                        beforeConnect: (client) => {
                            return new Promise((resolve, reject) => {
                                this.authService.tokenGetter().then(token => {
                                    client.configure({
                                        brokerURL: environment.events,
                                        heartbeatIncoming: 10000,
                                        heartbeatOutgoing: 10000,
                                        reconnectDelay: 1000,
                                        connectHeaders: {
                                            'Authorization': 'Bearer ' + token,
                                            'campaign': campaign
                                        },
                                        // debug: (message) => {
                                        //     this.logger.debug(message);
                                        // }
                                    });
                                    resolve();
                                });
                            });
                        }
                    });
                    this.activate();
                    return of(websocketActivated());
                } else {
                    this.deactivate();
                    return EMPTY;
                }
            })
        )
    );

    connectedEffect$ = createEffect(() =>
        this.connectionState$.pipe(
            map(stompState => {
                switch (stompState) {
                    case RxStompState.CLOSED:
                    case RxStompState.CLOSING:
                        return websocketClosed();
                    case RxStompState.CONNECTING:
                    case RxStompState.OPEN:
                        return websocketOpened();
                }
            })
        )
    );

    generalTopic = createEffect(() =>
        this.actions$.pipe(
            ofType(websocketOpened),
            switchMap(() => this.topic()
                .pipe(
                    map((event) => timelineEvent({event}))
                )
            )
        )
    );

    stateTopic = createEffect(() =>
        this.actions$.pipe(
            ofType(websocketOpened),
            switchMap((action) => this.topic('state')
                .pipe(
                    map((update) => {
                        switch (update.state) {
                            case State.Message:
                            case State.Action:
                                return stateUpdate({character: update.id, state: update.state});
                            case State.Idle:
                                return stateIdle({character: update.id});
                        }
                        return stateError();
                    })
                )
            )
        )
    );

    gmTopic = createEffect(() =>
        this.actions$.pipe(
            ofType(websocketOpened),
            withLatestFrom(this.store.select(isGameMaster)),
            filter(([action, gameMaster]) => gameMaster),
            switchMap(([action, gameMaster]) => this.topic('gm')
                .pipe(
                    map((event) => timelineEvent({event}))
                )
            )
        )
    );

    characterRefreshTopic = createEffect(() =>
        this.actions$.pipe(
            ofType(websocketOpened),
            switchMap(() => this.topic('characters')
                .pipe(
                    map(() => loadCharacters())
                )
            )
        )
    );

    loadEvents = createEffect(() =>
        this.actions$.pipe(
            ofType(websocketOpened),
            withLatestFrom(this.store.select(selectInitialized)),
            switchMap(([_, initialized]) => initialized ? of(synchronize()) : of(initialize()))
        )
    );

    reconnect$ = createEffect(() =>
            this.actions$.pipe(
                ofType(reconnect),
                tap(_ => {
                    this.deactivate();
                })
            ),
        {dispatch: false}
    );

    myCharactersTopic$ = createEffect(() =>
        this.actions$.pipe(
            ofType(websocketOpened),
            switchMap(() =>
                this.store.pipe(
                    select(selectMyCharacterIds), // switch to the current list of characters
                    switchMap(characters =>
                        from(
                            characters.map(id => this.topic('pc', id))
                        ).pipe(
                            mergeMap(observables => observables)
                        )
                    ),
                    map(event => timelineEvent({event: event}))
                )
            )
        )
    );

    timelineEvent = createEffect(() =>
        this.actions$.pipe(
            ofType(timelineEvent),
            switchMap((action) => {
                if (action.event.type === 'delete') {
                    return of(timelineDelete({id: action.event.id, count: action.event.count}));
                } else {
                    return this.store.pipe(
                        select(selectEventById(action.event.event.id)),
                        take(1),
                        switchMap((event: Event) => {
                            if (event) {
                                return of(timelineUpdate({event: action.event.event}));
                            } else {
                                return of(timelineCreate({event: action.event.event, count: action.event.count}));
                            }
                        })
                    );
                }

            })
        )
    );

    private topic(...destination: string[]): Observable<any> {
        return this.store.select(selectCurrentCampaignId)
            .pipe(
                map(campaign => ['/topic', campaign].concat(destination).join('/')),
                switchMap(topic => this.watch(topic)),
                map(response => JSON.parse(response.body))
            );
    }


    constructor(
        private actions$: Actions,
        private store: Store,
        private authService: AuthService,
        private logger: NGXLogger
    ) {
        super();
    }
}
